import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Progress } from "@/components/ui/progress"
import { useCallback, useEffect, useState } from "react"
import { getShopCustomerGroup } from "@/api/shop"
import { employeeListPlatformUserIdGroupNoChange, deleteEmployee } from "@/api/employee"
import { useShopStore } from "@/store/shop"
import { useAuthStore } from "@/store/auth"
import { toast } from 'sonner'
import type { CustomerGroup } from "@/types/shop"
import type { Employee } from "@/types/employee"

interface BulkGroupChangeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employees: Employee[]
  onComplete: () => void
}

export function BulkGroupChangeDialog({ open, onOpenChange, employees, onComplete }: BulkGroupChangeDialogProps) {
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [groupNo, setGroupNo] = useState<string>("")
  const [isFixedGroup, setIsFixedGroup] = useState<string>("T")
  
  // 프로그레스 상태
  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentEmployeeIndex, setCurrentEmployeeIndex] = useState(0)
  const [isCompleted, setIsCompleted] = useState(false)

  const { selectedShop } = useShopStore()
  const { user } = useAuthStore()
  const isSuperAdmin = user?.isSuperAdmin === true
  const allowedResignGroupNo = user?.allowedResignGroupNo ?? null
  const allowedResignIsFixedGroup = user?.allowedResignIsFixedGroup
  const isCafe24User = user?.platform === 'cafe24'
  const shopNo = isCafe24User && selectedShop ? selectedShop.shopNo : 1

  const loadCustomerGroups = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getShopCustomerGroup({ shopNo });
      
      if (response.code === 200 && response.data) {
        setCustomerGroups(response.data)
        if (!isSuperAdmin) {
          const allowedGroup = response.data.find((group) => group.groupNo === allowedResignGroupNo)
          const fallbackGroup = response.data[0]
          if (allowedGroup) {
            setGroupNo(allowedGroup.groupNo.toString())
          } else if (fallbackGroup) {
            setGroupNo(fallbackGroup.groupNo.toString())
          } else {
            setGroupNo("")
          }
          if (typeof allowedResignIsFixedGroup === "boolean") {
            setIsFixedGroup(allowedResignIsFixedGroup ? "T" : "F")
          }
        }
      } else {
        toast.error("고객 등급 정보를 가져오는데 실패했습니다.");
      }
    } catch (error) {
      console.error("고객 등급 조회 오류:", error);
      toast.error("고객 등급 정보를 가져오는 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  }, [allowedResignGroupNo, allowedResignIsFixedGroup, isSuperAdmin, shopNo]);
  
  // 다이얼로그 열릴 때 고객 그룹 로드 및 상태 초기화
  useEffect(() => {
    if (!open) return

    if (!isSuperAdmin) {
      if (allowedResignGroupNo !== null) {
        setGroupNo(allowedResignGroupNo.toString())
      }
      if (typeof allowedResignIsFixedGroup === "boolean") {
        setIsFixedGroup(allowedResignIsFixedGroup ? "T" : "F")
      }
    } else {
      setGroupNo("")
      setIsFixedGroup("T")
    }

    loadCustomerGroups()
    setProgress(0)
    setCurrentEmployeeIndex(0)
    setIsCompleted(false)
    setIsProcessing(false)
  }, [open, isSuperAdmin, allowedResignGroupNo, allowedResignIsFixedGroup, loadCustomerGroups])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!groupNo) {
      toast.error("회원 등급을 선택해주세요.");
      return;
    }

    setIsProcessing(true);
    setCurrentEmployeeIndex(0);

    try {
      const totalEmployees = employees.length;
      const totalSteps = totalEmployees + 1; // 등급 변경 + 삭제 API 1개
      let successCount = 0;
      let failCount = 0;

      // 1단계: 등급 변경
      for (let i = 0; i < employees.length; i++) {
        const employee = employees[i];
        setCurrentEmployeeIndex(i + 1);
        
        try {
          const response = await employeeListPlatformUserIdGroupNoChange(employee.id, {
            onlyUseLinked: "T",
            groupNo: parseInt(groupNo),
            isFixedGroup: isFixedGroup as "T" | "F"
          });

          if (response.code === 200) {
            successCount++;
          } else {
            failCount++;
            console.error(`임직원 ${employee.name} 등급 변경 실패:`, response.message);
          }
        } catch (error) {
          failCount++;
          console.error(`임직원 ${employee.name} 등급 변경 오류:`, error);
        }

        // 프로그레스 업데이트 (등급 변경 단계)
        const progressValue = ((i + 1) / totalSteps) * 100;
        setProgress(progressValue);
        
        // 짧은 딜레이를 주어 UI 업데이트가 보이도록 함
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // 2단계: 삭제 처리
      setCurrentEmployeeIndex(-1); // 삭제 단계임을 표시
      
      try {
        const employeeIds = employees.map(emp => emp.id.toString()).join(",");
        const deleteResponse = await deleteEmployee({ ids: employeeIds });
        
        if (deleteResponse.code === 200) {
          toast.success(`${employees.length}명의 임직원이 삭제되었습니다.`);
        } else {
          toast.error(deleteResponse.message || "임직원 삭제에 실패했습니다.");
        }
      } catch (error) {
        console.error("임직원 삭제 오류:", error);
        toast.error("임직원 삭제 중 오류가 발생했습니다.");
      }

      // 완료 상태로 변경
      setProgress(100);
      setIsCompleted(true);

      // 자동으로 닫지 않고 사용자가 확인 버튼을 누르기 까지 대기
      
    } catch (error) {
      console.error("회원 등급 일괄 변경 오류:", error);
      toast.error("회원 등급 변경 중 오류가 발생했습니다.");
    } finally {
      // 완료 상태가 아닌 경우(에러 발생 시)만 초기화
      if (!isCompleted) {
        setIsProcessing(false);
        setCurrentEmployeeIndex(0);
      }
    }
  };

  const handleClose = () => {
    if (isProcessing) return

    if (isSuperAdmin) {
      setGroupNo("")
      setIsFixedGroup("T")
    } else {
      setGroupNo(
        allowedResignGroupNo !== null ? allowedResignGroupNo.toString() : groupNo
      )
      setIsFixedGroup(
        typeof allowedResignIsFixedGroup === "boolean"
          ? allowedResignIsFixedGroup
            ? "T"
            : "F"
          : isFixedGroup
      )
    }
    setCurrentEmployeeIndex(0)
    setIsCompleted(false)
    onOpenChange(false)
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>회원 등급 일괄 변경</DialogTitle>
            <DialogDescription>
              선택된 {employees.length}명의 임직원 회원 등급을 일괄 변경합니다.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {isProcessing || isCompleted ? (
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  {isCompleted ? (
                    <span className="text-green-600 font-medium">✓ 처리 완료!</span>
                  ) : currentEmployeeIndex === -1 ? (
                    "임직원 삭제 중..."
                  ) : (
                    `${currentEmployeeIndex}/${employees.length} 등급 변경 중...`
                  )}
                </div>
                <Progress value={progress} className="w-full" />
                <div className="text-xs text-muted-foreground text-center">
                  {isCompleted ? (
                    <span className="text-green-600 font-medium">100% 완료</span>
                  ) : (
                    `${Math.round(progress)}% 완료`
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="grid gap-2">
                  <Label>회원 등급</Label>
                  <Select
                    value={groupNo}
                    onValueChange={setGroupNo}
                    disabled={isLoading || isProcessing || !isSuperAdmin}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={isLoading ? "로딩 중..." : "변경할 회원 등급 선택"} />
                    </SelectTrigger>
                    <SelectContent>
                      {customerGroups.map((group) => (
                        <SelectItem key={group.groupNo} value={group.groupNo.toString()}>
                          {group.groupName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label>회원 등급 고정 여부</Label>
                  <Select
                    value={isFixedGroup}
                    onValueChange={setIsFixedGroup}
                    disabled={isLoading || isProcessing || !isSuperAdmin}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="T">회원 등급 고정함</SelectItem>
                      <SelectItem value="F">회원 등급 고정안함</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                  <strong>주의사항:</strong>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• 선택된 임직원들의 연동된 회원 등급만 변경됩니다.</li>
                    <li>• 비연동 상태의 회원은 처리되지 않습니다.</li>
                    <li>• 처리 완료 후 선택된 임직원들이 삭제됩니다.</li>
                  </ul>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            {isCompleted ? (
              <Button
                type="button"
                onClick={() => {
                  // 다이얼로그 먼저 닫기
                  onOpenChange(false);
                  
                  // 다이얼로그가 닫힌 후에 상태 초기화와 onComplete 호출
                  setTimeout(() => {
                    // 상태 초기화
                    setIsProcessing(false);
                    setCurrentEmployeeIndex(0);
                    setIsCompleted(false);
                    
                    onComplete();
                  }, 200);
                }}
              >
                확인
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isLoading || isProcessing}
                >
                  취소
                </Button>
                <Button 
                  type="submit" 
                  disabled={isLoading || isProcessing || !groupNo}
                >
                  {isProcessing ? "처리 중..." : "변경"}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
